import React, { createContext, useContext, useCallback } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import { normalizeDate } from '../utils/formatDate';

const AuditOperationsApiContext = createContext(null);

export function AuditOperationsApiProvider({ children }) {
  const { get, post } = useApiService(CONFIG.SERVER_ORGANIZATION);

  const getAuditOperations = useCallback(
    async (skip, limit, start_date, end_date, filters = {}) => {
      try {
        const response = await post('/v1/audit/operation/search', {
          ...filters,
          skip,
          limit,
          start_date: normalizeDate(start_date),
          end_date: normalizeDate(end_date),
        });

        return response.data;
      } catch (error) {
        console.error('Error fetching audit operations:', error);
        throw error;
      }
    },
    [post],
  );

  const getAuditOperationById = useCallback(
    async (auditId) => {
      try {
        const response = await get(`/v1/audit/operation/${auditId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching audit operation by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getAuditOperationTypes = useCallback(async () => {
    try {
      const response = await get(`/v1/audit/operation/types`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit operation types:', error);
      throw error;
    }
  }, [get]);

  const exportAuditOperations = useCallback(
    async ({ start_date, end_date, ...filters }) => {
      const response = await post(
        '/v1/audit/operation/export',
        { ...filters, start_date: normalizeDate(start_date), end_date: normalizeDate(end_date) },
        { responseType: 'blob' },
      );
      return response.data;
    },
    [post],
  );

  return (
    <AuditOperationsApiContext.Provider
      value={{
        getAuditOperations,
        getAuditOperationById,
        getAuditOperationTypes,
        exportAuditOperations,
      }}
    >
      {children}
    </AuditOperationsApiContext.Provider>
  );
}

export function useAuditOperationsApi() {
  const context = useContext(AuditOperationsApiContext);
  if (!context) {
    throw new Error('useAuditOperationsApi must be used within a AuditOperationsApiProvider');
  }
  return context;
}
